import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { getApiBase } from '../../utils/apiBase';
import { toast } from 'sonner';
import { DEFAULT_MEMORIAL_VISIBILITY, MEMORIAL_COPY, mergeVisibility } from '../../utils/memorial';

const API = getApiBase();

const VISIBILITY_FIELDS = [
  { key: 'show_stories', label: 'Life stories', hint: 'Written memories on the tribute page' },
  { key: 'show_voice', label: 'Voice notes', hint: 'Audio keepsake playback (when enabled)' },
  { key: 'show_documents', label: 'Documents', hint: 'Non-vault documents only' },
  { key: 'show_timeline', label: 'Tribute timeline', hint: 'Key dates and memory markers' },
  { key: 'show_biography', label: 'Biography', hint: 'Life summary from the profile' },
  { key: 'show_video_links', label: 'Video memories', hint: 'Linked external videos' },
  { key: 'public_tribute_enabled', label: 'Legacy share link (/remember)', hint: 'Optional. New memorial pages use /memorial after you publish.' },
];

export function MemorialVisibilityDialog({ open, onOpenChange, personId, person, stories = [], onSaved }) {
  const [visibility, setVisibility] = useState(DEFAULT_MEMORIAL_VISIBILITY);
  const [featuredStoryId, setFeaturedStoryId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !person) return;
    const vis = mergeVisibility(person.memorial_settings?.visibility || person.memorial_summary?.visibility);
    setVisibility(vis);
    setFeaturedStoryId(person.memorial_settings?.featured_story_id || person.memorial_summary?.featured_story_id || '');
  }, [open, person]);

  const handleSave = async () => {
    if (!personId) return;
    if (visibility.public_tribute_enabled) {
      const ok = window.confirm(
        'Enable the public tribute page?\n\nOnly the items you checked below will be visible. Vault documents are never included.'
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/individuals/${personId}/memorial/visibility`, {
        visibility,
        featured_story_id: featuredStoryId || null,
      });
      toast.success('Memorial content settings saved');
      onSaved?.(data.memorial);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save tribute visibility');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="memorial-visibility-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif">{MEMORIAL_COPY.configureVisibility}</DialogTitle>
          <DialogDescription>
            Choose what may appear on the public memorial page. Nothing is shared until you publish the memorial.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {VISIBILITY_FIELDS.map(({ key, label, hint }) => (
            <label key={key} className="flex items-start gap-3 rounded-lg border border-border/40 p-3">
              <Checkbox
                checked={Boolean(visibility[key])}
                onCheckedChange={(v) => setVisibility((prev) => ({ ...prev, [key]: Boolean(v) }))}
                className="mt-0.5"
              />
              <span className="min-w-0">
                <span className="text-sm font-medium">{label}</span>
                <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span>
              </span>
            </label>
          ))}
          {stories.length > 0 ? (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Featured life story (optional)</Label>
              <select
                className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm"
                value={featuredStoryId}
                onChange={(e) => setFeaturedStoryId(e.target.value)}
              >
                <option value="">None</option>
                {stories.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.display_title || s.question_text || 'Story'}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Save visibility
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MemorialRemembranceSection({
  personId,
  person,
  canManage,
  deathDate,
  onDeathDateChange,
  onSaved,
  onOpenVisibility,
}) {
  const [remembered, setRemembered] = useState(false);
  const [memorialMode, setMemorialMode] = useState(false);
  const [remembranceLine, setRemembranceLine] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!person) return;
    setRemembered(person.life_state === 'remembered' || person.memorial_summary?.life_state === 'remembered');
    setMemorialMode(Boolean(person.memorial_mode_enabled || person.memorial_summary?.memorial_mode_enabled));
    setRemembranceLine(person.remembrance_line || person.memorial_summary?.remembrance_line || '');
  }, [person]);

  const saveMemorial = async (patch) => {
    if (!personId || !canManage) return;
    setSaving(true);
    try {
      const body = {
        ...patch,
        death_date: deathDate || null,
      };
      const { data } = await axios.patch(`${API}/individuals/${personId}/memorial`, body);
      toast.success('Remembrance settings saved');
      onSaved?.(data.memorial);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save remembrance settings');
    } finally {
      setSaving(false);
    }
  };

  if (!canManage && !remembered) return null;

  return (
    <div className="col-span-2 space-y-4 rounded-xl border border-[hsl(38_14%_86%)]/70 bg-[hsl(42_28%_99%)]/50 p-4" data-testid="memorial-remembrance-section">
      <div>
        <p className="text-sm font-medium">Life &amp; remembrance</p>
        <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
          Optional memorial state — separate from archiving or removing someone from the tree.
        </p>
      </div>
      {canManage ? (
        <>
          <label className="flex items-start gap-3">
            <Checkbox
              checked={remembered}
              onCheckedChange={(v) => {
                const next = Boolean(v);
                setRemembered(next);
                if (!next) setMemorialMode(false);
                saveMemorial({
                  life_state: next ? 'remembered' : 'living',
                  memorial_mode_enabled: next ? memorialMode : false,
                  remembrance_line: remembranceLine.trim() || null,
                });
              }}
              disabled={saving}
            />
            <span className="text-sm">{MEMORIAL_COPY.markDeceased}</span>
          </label>
          <div className="space-y-1.5">
            <Label htmlFor="memorial-death-date" className="text-xs text-muted-foreground">
              Date of passing
            </Label>
            <Input
              id="memorial-death-date"
              type="date"
              value={deathDate || ''}
              onChange={(e) => onDeathDateChange?.(e.target.value)}
              onBlur={() => remembered && saveMemorial({ life_state: 'remembered', memorial_mode_enabled: memorialMode, remembrance_line: remembranceLine.trim() || null })}
              disabled={!remembered}
              className="h-9"
            />
          </div>
          <label className="flex items-start gap-3">
            <Checkbox
              checked={memorialMode}
              disabled={!remembered || saving}
              onCheckedChange={(v) => {
                const next = Boolean(v);
                setMemorialMode(next);
                saveMemorial({
                  life_state: 'remembered',
                  memorial_mode_enabled: next,
                  remembrance_line: remembranceLine.trim() || null,
                });
              }}
            />
            <span className="text-sm">{MEMORIAL_COPY.memorialMode}</span>
          </label>
          <div className="space-y-1.5">
            <Label htmlFor="remembrance-line" className="text-xs text-muted-foreground">
              {MEMORIAL_COPY.remembranceLine}
            </Label>
            <Textarea
              id="remembrance-line"
              value={remembranceLine}
              onChange={(e) => setRemembranceLine(e.target.value)}
              onBlur={() =>
                remembered &&
                saveMemorial({
                  life_state: 'remembered',
                  memorial_mode_enabled: memorialMode,
                  remembrance_line: remembranceLine.trim() || null,
                })
              }
              rows={2}
              placeholder="A gentle line for family and visitors"
              className="font-serif text-sm"
              disabled={!remembered}
            />
          </div>
          {remembered && memorialMode ? (
            <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={onOpenVisibility}>
              {MEMORIAL_COPY.configureVisibility}
            </Button>
          ) : null}
        </>
      ) : remembered ? (
        <p className="text-sm text-muted-foreground">{MEMORIAL_COPY.rememberedHere}</p>
      ) : null}
    </div>
  );
}

export default MemorialRemembranceSection;
